/**
 * Weekly timesheet helpers — turns raw sign_ins into a per-person, per-day
 * hours grid for the wages run.
 *
 * Mon-Sun is the default week (UK construction payroll convention).
 */

import type { SignIn } from './db';

const MS_PER_HOUR = 1000 * 60 * 60;

export interface WeekRange {
  startIso: string;     // Monday 00:00:00 ISO
  endIso: string;       // Following Monday 00:00:00 ISO (exclusive)
  startDate: Date;      // Monday 00:00:00 local
  endDate: Date;        // Sunday 23:59:59 local
  label: string;        // "5 — 11 May 2026"
}

/** Monday-Sunday week containing `d`. */
export function weekOf(d: Date): WeekRange {
  const start = new Date(d);
  start.setHours(0, 0, 0, 0);
  const day = start.getDay() || 7;        // Sun=7
  if (day !== 1) start.setDate(start.getDate() - (day - 1));
  const end = new Date(start);
  end.setDate(start.getDate() + 7);       // exclusive
  const endDate = new Date(start);
  endDate.setDate(start.getDate() + 6);
  endDate.setHours(23, 59, 59, 999);

  const fmt = (x: Date) => x.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  const fmtFull = (x: Date) => x.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const sameMonth = start.getMonth() === endDate.getMonth();
  const label = sameMonth
    ? `${start.getDate()}–${endDate.getDate()} ${fmtFull(endDate).replace(/^\d+\s/, '')}`
    : `${fmt(start)} — ${fmtFull(endDate)}`;

  return {
    startIso: start.toISOString(),
    endIso: end.toISOString(),
    startDate: start,
    endDate,
    label,
  };
}

export function previousWeek(w: WeekRange): WeekRange {
  const d = new Date(w.startDate);
  d.setDate(d.getDate() - 7);
  return weekOf(d);
}

export function nextWeek(w: WeekRange): WeekRange {
  const d = new Date(w.startDate);
  d.setDate(d.getDate() + 7);
  return weekOf(d);
}

/** Hours worked between time_in and time_out. Returns null if open (not signed out). */
export function hoursWorked(s: SignIn): number | null {
  if (!s.time_out) return null;
  const inMs = new Date(s.time_in).getTime();
  const outMs = new Date(s.time_out).getTime();
  if (Number.isNaN(inMs) || Number.isNaN(outMs) || outMs <= inMs) return null;
  return (outMs - inMs) / MS_PER_HOUR;
}

/** Local-time "yyyy-mm-dd" key for grouping by day. */
function dayKey(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export interface PersonRow {
  name: string;          // identity key — full_name + ' / ' + company
  fullName: string;
  company: string;
  /** Hours worked per day, keyed by yyyy-mm-dd. Sums multiple sign-ins on the same day. */
  hoursByDay: Record<string, number>;
  /** Sign-ins on each day — used to flag rows where someone forgot to sign out. */
  rowsByDay: Record<string, SignIn[]>;
  totalHours: number;
  hasOpen: boolean;       // true if any sign-in on any day has no time_out
}

export interface TimesheetGrid {
  /** yyyy-mm-dd keys for the 7 days, Mon → Sun. */
  dayKeys: string[];
  /** Date objects for the 7 days. */
  dayDates: Date[];
  /** People rows, sorted alphabetically. */
  rows: PersonRow[];
  /** Daily totals across all people. */
  totalsByDay: Record<string, number>;
  totalHours: number;
}

export function buildTimesheet(week: WeekRange, signIns: SignIn[]): TimesheetGrid {
  const dayDates: Date[] = [];
  const dayKeys: string[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(week.startDate);
    d.setDate(d.getDate() + i);
    dayDates.push(d);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    dayKeys.push(`${y}-${m}-${day}`);
  }

  const peopleByKey = new Map<string, PersonRow>();
  const totalsByDay: Record<string, number> = Object.fromEntries(dayKeys.map((k) => [k, 0]));

  for (const s of signIns) {
    const key = `${(s.full_name || '').trim().toLowerCase()}|${(s.company || '').trim().toLowerCase()}`;
    let p = peopleByKey.get(key);
    if (!p) {
      p = {
        name: key,
        fullName: (s.full_name || '').trim() || '(unknown)',
        company: (s.company || '').trim(),
        hoursByDay: {},
        rowsByDay: {},
        totalHours: 0,
        hasOpen: false,
      };
      peopleByKey.set(key, p);
    }
    const dk = dayKey(s.time_in);
    if (!dayKeys.includes(dk)) continue;        // outside the selected week
    const h = hoursWorked(s);
    if (h === null) p.hasOpen = true;
    p.hoursByDay[dk] = (p.hoursByDay[dk] ?? 0) + (h ?? 0);
    p.rowsByDay[dk] = [...(p.rowsByDay[dk] ?? []), s];
    p.totalHours += h ?? 0;
    totalsByDay[dk] += h ?? 0;
  }

  const rows = [...peopleByKey.values()].sort((a, b) => a.fullName.localeCompare(b.fullName));
  const totalHours = Object.values(totalsByDay).reduce((a, b) => a + b, 0);
  return { dayKeys, dayDates, rows, totalsByDay, totalHours };
}

export function fmtHours(h: number): string {
  if (h <= 0) return '';
  return h.toFixed(2).replace(/\.00$/, '');
}

export function fmtTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

// ---------- CSV exports ------------------------------------------------------

/**
 * Per-person weekly summary. Plain CSV — one row per person, one column per
 * day of the week, plus total. Drop it into a spreadsheet, hand it to an
 * accountant, import it into whatever payroll tool uses CSV. We don't claim
 * any specific accounting-software integration.
 */
export function summaryCsv(grid: TimesheetGrid, week: WeekRange): string {
  const headers = ['Employee', 'Company', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun', 'Total hours', 'Notes'];
  const rows: string[][] = [headers];
  for (const p of grid.rows) {
    const cols = [
      p.fullName, p.company,
      fmtHours(p.hoursByDay[grid.dayKeys[0]] ?? 0),
      fmtHours(p.hoursByDay[grid.dayKeys[1]] ?? 0),
      fmtHours(p.hoursByDay[grid.dayKeys[2]] ?? 0),
      fmtHours(p.hoursByDay[grid.dayKeys[3]] ?? 0),
      fmtHours(p.hoursByDay[grid.dayKeys[4]] ?? 0),
      fmtHours(p.hoursByDay[grid.dayKeys[5]] ?? 0),
      fmtHours(p.hoursByDay[grid.dayKeys[6]] ?? 0),
      fmtHours(p.totalHours),
      p.hasOpen ? 'Open sign-in — check before paying' : '',
    ];
    rows.push(cols);
  }
  const csv = rows.map((r) => r.map(csvEscape).join(',')).join('\n');
  return `# Site sign-in summary — week of ${week.label}\n${csv}\n`;
}

/**
 * One row per sign-in (start/end times), useful when the wages person wants
 * to verify the raw data behind a total.
 */
export function detailedCsv(signIns: SignIn[]): string {
  const headers = ['Date', 'Employee', 'Company', 'Trade/role', 'Time in', 'Time out', 'Hours'];
  const rows: string[][] = [headers];
  for (const s of signIns) {
    const d = new Date(s.time_in);
    const dateStr = d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const h = hoursWorked(s);
    rows.push([
      dateStr,
      s.full_name || '',
      s.company || '',
      s.trade_role || '',
      fmtTime(s.time_in),
      fmtTime(s.time_out),
      h === null ? '(not signed out)' : fmtHours(h),
    ]);
  }
  return rows.map((r) => r.map(csvEscape).join(',')).join('\n') + '\n';
}

function csvEscape(v: string): string {
  if (v == null) return '';
  if (v.includes(',') || v.includes('"') || v.includes('\n')) {
    return `"${v.replace(/"/g, '""')}"`;
  }
  return v;
}

// ---------- PDF export -------------------------------------------------------

import jsPDF from 'jspdf';

interface PdfOpts {
  orgName: string;
  siteName: string;
  week: WeekRange;
  grid: TimesheetGrid;
}

const PDF_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Simple weekly timesheet PDF. A4 portrait. Plain text rows so the file size
 * stays small and prints fine on a phone.
 */
export function generateTimesheetPdf(opts: PdfOpts): Blob {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const MARGIN = 14;
  let y = MARGIN;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text(`Sign-in log / hours — ${opts.siteName}`, MARGIN, y);
  y += 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(80, 80, 80);
  doc.text(`Week of ${opts.week.label}`, MARGIN, y);
  y += 4;
  doc.text(`Generated by ${opts.orgName} · ${new Date().toLocaleString('en-GB')}`, MARGIN, y);
  y += 8;

  // Table header
  const colWidths = [62, 14, 14, 14, 14, 14, 14, 14, 18];   // Person + 7 days + Total
  const headers = ['Person', ...PDF_DAYS, 'Total'];
  doc.setDrawColor(200, 200, 200);
  doc.setFillColor(240, 240, 240);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  let x = MARGIN;
  doc.rect(MARGIN, y, colWidths.reduce((a, b) => a + b, 0), 6, 'F');
  for (let i = 0; i < headers.length; i++) {
    const align = i === 0 ? 'left' : 'right';
    const tx = align === 'left' ? x + 1.5 : x + colWidths[i] - 1.5;
    doc.text(headers[i], tx, y + 4, { align: align as 'left' | 'right' });
    x += colWidths[i];
  }
  y += 6;

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(20, 20, 20);

  if (opts.grid.rows.length === 0) {
    doc.setTextColor(120, 120, 120);
    doc.text('No sign-ins this week.', MARGIN + 1, y + 6);
    y += 14;
  } else {
    for (const p of opts.grid.rows) {
      // New page if running off the bottom
      if (y > 270) {
        doc.addPage();
        y = MARGIN;
      }
      x = MARGIN;
      const cells = [
        p.fullName + (p.company ? ` (${p.company})` : '') + (p.hasOpen ? ' [open]' : ''),
        ...opts.grid.dayKeys.map((dk) => fmtHours(p.hoursByDay[dk] ?? 0) || '—'),
        fmtHours(p.totalHours) || '—',
      ];
      for (let i = 0; i < cells.length; i++) {
        const align = i === 0 ? 'left' : 'right';
        const tx = align === 'left' ? x + 1.5 : x + colWidths[i] - 1.5;
        doc.text(String(cells[i]), tx, y + 4, { align: align as 'left' | 'right', maxWidth: colWidths[i] - 3 });
        x += colWidths[i];
      }
      // Subtle row separator
      doc.setDrawColor(230, 230, 230);
      doc.line(MARGIN, y + 6, MARGIN + colWidths.reduce((a, b) => a + b, 0), y + 6);
      y += 6;
    }

    // Totals row
    doc.setFont('helvetica', 'bold');
    x = MARGIN;
    const tot = ['Daily total', ...opts.grid.dayKeys.map((dk) => fmtHours(opts.grid.totalsByDay[dk] ?? 0) || '—'), fmtHours(opts.grid.totalHours) || '—'];
    for (let i = 0; i < tot.length; i++) {
      const align = i === 0 ? 'left' : 'right';
      const tx = align === 'left' ? x + 1.5 : x + colWidths[i] - 1.5;
      doc.text(String(tot[i]), tx, y + 4, { align: align as 'left' | 'right' });
      x += colWidths[i];
    }
    y += 8;
  }

  // Footer note
  if (opts.grid.rows.some((p) => p.hasOpen)) {
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(150, 50, 50);
    doc.setFontSize(9);
    doc.text('[open] = signed in but not signed out — hours not counted, please verify before paying.', MARGIN, y + 4);
  }

  return doc.output('blob') as unknown as Blob;
}
